<template>
  <div>
    <div>
      <div v-if="loading" class="dot-opacity-loader" style="z-index: 5">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div id="sensorChartContainer" style="height: 300px" class="mt-3"></div>
    </div>
  </div>
</template>

<script>
import sensorValues from '@/api/boxinfo/sensorValues.gql';
import moment from 'moment';
import CanvasJS from '@/plugins/canvasjs.min';
const canvas = CanvasJS.Chart ? CanvasJS : window.CanvasJS;

export default {
  name: 'SensorCanvasPlot',
  props: {
    sensorId: Number,
    period: Object,
    minPeriod: Number,
    maxPeriod: Number,
  },
  apollo: {
    sensorData: {
      query: sensorValues,
      variables() {
        const startTime = this.start;
        const endTime = this.end;
        return {
          id: this.sensorId,
          startTime,
          endTime,
          sampleCount: 2000,
        };
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        return ((queryData || {}).sensor || [])[0] || {};
      },
      skip() {
        return !this.sensorId || !this.period || !this.period.dateStart;
      },
      pollInterval() {
        return !this.rangeMax && !this.period.dateEnd ? 30000 : undefined;
      },
    },
  },
  data: () => {
    return {
      values: [],
      rangeMin: null,
      rangeMax: null,
    };
  },
  computed: {
    start() {
      return this.rangeMin
        ? moment(this.rangeMin).format()
        : this.minPeriod
        ? moment(this.minPeriod).format()
        : this.period.dateStart;
    },
    end() {
      return this.rangeMax
        ? moment(this.rangeMax).format()
        : this.maxPeriod
        ? moment(this.maxPeriod).format()
        : this.period.dateEnd;
    },
    unitName() {
      return (((this.sensorData || {}).type || {}).measurementUnit || {}).shortName || '';
    },
    chartType() {
      return ((this.sensorData || {}).type || {}).chartType || '';
    },
    sensorName() {
      return (this.sensorData || {}).name || this.$t('box.value');
    },
    dataSeries() {
      return [
        {
          type: 'splineArea',
          color: '#FF6D0B',
          lineThickness: 1,
          dataPoints: this.values,
        },
      ];
    },
    chartOptionsCj() {
      const vm = this;
      return {
        theme: 'dark2',
        zoomEnabled: true,
        zoomType: 'x',
        backgroundColor: 'rgb(25, 28, 36)',
        axisX: {
          minimum: vm.minDate,
          maximum: vm.maxDate,
          lineThickness: 1,
          gridThickness: 0,
          gridColor: '#000000',
          lineColor: 'grey',
          labelFontColor: 'grey',
          tickColor: 'grey',
          labelMaxWidth: 40,
          labelFontSize: 10,
          labelFormatter: function (e) {
            const label = moment(e.value).locale(vm.locale).format('DD MMM HH:mm');
            return label;
          },
        },
        axisY: {
          minimum: 0,
          lineThickness: 0,
          tickThickness: 0,
          gridColor: '#000000',
          labelFontColor: 'grey',
          includeZero: true,
        },
        toolTip: {
          contentFormatter: function (e) {
            const value = `${vm.formatNumber(e.entries[0].dataPoint.y)} ${vm.unitName}`;
            const start = moment(e.entries[0].dataPoint.x)
              .locale(vm.locale)
              .format('DD.MM.YYYY HH:mm:ss');
            return `${start}: <b><font color="green">${value}</font></b>`;
          },
        },
        rangeChanged: function (e) {
          vm.rangeMin = e.axisX[0].viewportMinimum;
          vm.rangeMax = e.axisX[0].viewportMaximum;
        },
        data: vm.dataSeries,
      };
    },
    minDate() {
      return this.minPeriod
        ? this.minPeriod
        : this.period.dateStart
        ? new Date(this.period.dateStart).getTime()
        : new Date().getTime();
    },
    maxDate() {
      return this.maxPeriod
        ? this.maxPeriod
        : this.period.dateEnd
        ? new Date(this.period.dateEnd).getTime()
        : new Date().getTime();
    },
    loading() {
      return this.$apollo.queries.sensorData.loading;
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  watch: {
    sensorData(data) {
      this.values.length = 0;
      if (data && data.values && data.values.length) {
        data.values.forEach((i) => {
          this.values.push({
            x: new Date(moment(i.timeStamp).valueOf()),
            y: i.value,
          });
        });
      }
      this.updateChart(false);
    },
    minDate() {
      this.updateChart(true);
    },
    period() {
      this.updateChart(true);
    },
    sensorId() {
      this.updateChart(true);
    },
    locale() {
      this.updateChart(true);
    },
  },
  updated() {
    this.updateChart(false);
  },
  methods: {
    updateChart(newChart) {
      const chartId = 'sensorChartContainer';
      if (document.getElementById(chartId)) {
        if (!this.chart || newChart) {
          this.rangeMin = null;
          this.rangeMax = null;
          this.chart = new canvas.Chart(chartId, this.chartOptionsCj);
        }
        this.chart.render();
      }
    },
    formatNumber(v) {
      return Number(v.toPrecision(4)).toLocaleString();
    },
  },
};
</script>

<style>
#sensorChartContainer .canvasjs-chart-toolbar > :first-child {
  display: none !important;
}
</style>
